import {
  Box,
  Center,
  Flex,
  Icon,
  Switch,
  Text,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react'
import { skipToken } from '@reduxjs/toolkit/query'
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import Loading from '../../components/shared-components/Loading'
import { Prescription } from '../../types'
import { CeasePrescriptionModal } from './components/CeasePrescriptionModal'
import { ReIssuePrescriptionModal } from './components/ReIssuePrescriptionModal'
import CancelPrescriptionModal from './components/CancelPrescriptionModal'
import { useGetAllPrescriptionsQuery } from '../../services/prescriptions'
import RCPagination from '../../components/RCPagination'
import SearchInput from '../../components/form/SearchInput'
import PrescriptionTable from './components/PrescriptionTable'
import { RenewPrescriptionLoadingModal } from './components/RenewPrescriptionLoadingModal'
import { LocationFilter } from '../../features/locations/components/LocationFilter'
import { useAppDispatch, useAppSelector } from '../../app/typedReduxHooks'
import { RootState } from '../../app/store'
import { LimitedPrescriberProfile } from '../../features/users/UsersSlice'
import { PrescriberSelector } from '../../components/PrescriberSelector'
import UnauthorizedPage from '../../components/UnauthorizedPage'
import { Permission } from '../../types/permission'
import { useHasEntitlement } from '../OrganizationProfilePage/planEntitlements'
import { FeatureKey } from '../../types/organization-entitlements'
import { FiUsers } from 'react-icons/fi'
import { setShowOnlyMyPrescriptions } from '../NewScriptPage/ScriptForm/PrescriberSlice'
import { useSubscriptionGuard } from '../../hooks/useSubscriptionGuard'
import { prefillPrescriptionForRenew } from '../../features/renew-prescription/prefillPrescriptionForRenew'
import { routes } from '../routes'
import { useCanPrescribeFor } from '../../features/ehr-launch/useCanPrescribeFor'

const PrescriptionsPage = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const toast = useToast()
  const { runWithValidSubscription } = useSubscriptionGuard()
  const { patientId, prescriberId } = useParams()
  const [searchParams, setSearchParams] = useSearchParams()
  const [page, setPage] = useState(1)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedLocationId, setSelectedLocationId] = useState<string>('')
  const showOnlyMyPrescriptions = useAppSelector(
    ({ prescriber }: RootState) => prescriber.showOnlyMyPrescriptions
  )

  const currentUserPrescriberProfile = useAppSelector(
    ({ prescriber }: RootState) => prescriber?.prescriber
  )
  const isSharedPatientsFeatureOn = useHasEntitlement(FeatureKey.SHARED_PATIENTS)
  const isMultipleLocationsFeatureOn = useHasEntitlement(FeatureKey.MULTIPLE_LOCATIONS)

  const users = useAppSelector((state: RootState) => state.users?.data || [])
  const prescribers = useMemo(() => users.filter((user) => user.prescriber), [users])
  const isSharePrescriptionSettingOn = useAppSelector(
    (state) => state.organization?.data?.settings?.enableSharePrescription
  )

  const permissions = useAppSelector((state) => state.authentication.permissions)
  const isPrescriptionAdmin = !!permissions?.includes(Permission.PRESCRIPTION_MANAGEMENT)
  const canRenewScript = !!permissions?.includes(Permission.PRESCRIBING)
  const organizationSettings = useAppSelector((state) => state.organization?.data?.settings)
  const defaultPrescriptionDeliveryMethod = organizationSettings?.defaultPrescriptionDeliveryMethod
  const authenticatedProvider = useAppSelector((state) => state.authenticatedProvider)
  const canPrescribeFor = useCanPrescribeFor()

  const routePrescriber: LimitedPrescriberProfile | null = useMemo(() => {
    if (!prescriberId) return null
    return prescribers.find((u) => u.prescriber?.id === prescriberId)?.prescriber ?? null
  }, [prescribers, prescriberId])

  const [adminScopePrescriber, setAdminScopePrescriber] = useState<LimitedPrescriberProfile | null>(
    null
  )

  const legacyDefaultPrescriber: LimitedPrescriberProfile | null = useMemo(
    () => prescribers.find((u) => u.prescriber?.active)?.prescriber ?? null,
    [prescribers]
  )

  const prescriberActiveById = useMemo(() => {
    const map = new Map<string, boolean>()
    users.forEach((u) => {
      if (u.prescriber?.id) map.set(u.prescriber.id, u.prescriber.active === true)
    })
    return map
  }, [users])

  const canSharePrescriptionsAcrossOrg = isSharedPatientsFeatureOn && !!isSharePrescriptionSettingOn

  const [reIssueModalIsOpen, setReIssueModalIsOpen] = useState<boolean>(false)
  const [isCancelModalOpen, setIsCancelModalOpen] = useState<boolean>(false)
  const [renewingScid, setRenewingScid] = useState<string | null>(null)
  const [selectedPrescription, setSelectedPrescription] = useState<Prescription | undefined>(
    undefined
  )
  const [selectedScid, setSelectedScid] = useState<string>('')
  const { isOpen: isCeaseOpen, onOpen: onCeaseOpen, onClose: onCeaseClose } = useDisclosure()

  const currentUserPrescriberId = currentUserPrescriberProfile?.id

  useEffect(() => {
    const urlSearchTerm = searchParams.get('search')
    setSearchTerm(urlSearchTerm || '')
  }, [searchParams])

  useEffect(() => {
    if (showOnlyMyPrescriptions && !currentUserPrescriberId && !currentUserPrescriberProfile) {
      dispatch(setShowOnlyMyPrescriptions(false))
    }
  }, [currentUserPrescriberId, dispatch, showOnlyMyPrescriptions, currentUserPrescriberProfile])

  useEffect(() => {
    if (!isMultipleLocationsFeatureOn) {
      setSelectedLocationId('')
    }
  }, [isMultipleLocationsFeatureOn])

  const prescriptionScope = useMemo(() => {
    if (!isSharedPatientsFeatureOn) {
      const scopedPrescriberId =
        routePrescriber?.id ??
        adminScopePrescriber?.id ??
        currentUserPrescriberId ??
        legacyDefaultPrescriber?.id
      return scopedPrescriberId ? { prescriberId: scopedPrescriberId, patientId } : null
    }

    if (!isSharePrescriptionSettingOn) {
      const scopedPrescriberId = isPrescriptionAdmin
        ? routePrescriber?.id ??
          adminScopePrescriber?.id ??
          currentUserPrescriberId ??
          legacyDefaultPrescriber?.id
        : currentUserPrescriberId
      return scopedPrescriberId ? { prescriberId: scopedPrescriberId, patientId } : null
    }

    if (showOnlyMyPrescriptions) {
      return currentUserPrescriberId ? { prescriberId: currentUserPrescriberId, patientId } : null
    }
    return { patientId }
  }, [
    isSharedPatientsFeatureOn,
    isSharePrescriptionSettingOn,
    isPrescriptionAdmin,
    showOnlyMyPrescriptions,
    routePrescriber?.id,
    adminScopePrescriber?.id,
    currentUserPrescriberId,
    legacyDefaultPrescriber?.id,
    patientId,
  ])

  useEffect(() => {
    setPage(1)
  }, [showOnlyMyPrescriptions, prescriptionScope?.prescriberId, selectedLocationId])

  const {
    isLoading,
    data: prescriptionsResponse,
    error,
  } = useGetAllPrescriptionsQuery(
    prescriptionScope
      ? {
          page: String(page),
          limit: '10',
          search: searchTerm,
          locationId:
            isMultipleLocationsFeatureOn && selectedLocationId ? selectedLocationId : undefined,
          ...prescriptionScope,
        }
      : skipToken
  )
  const { data: prescriptions, meta: prescriptionsPaginationData } = prescriptionsResponse || {}

  const openCeaseModal = (index: number) => {
    const scid = prescriptions?.[index]?.scid
    if (scid) {
      setSelectedScid(scid)
      onCeaseOpen()
    }
  }

  const openCancelModal = (index: number) => {
    const prescription = prescriptions?.[index]
    prescription && setSelectedPrescription(prescription)
    setIsCancelModalOpen(true)
  }

  const openReIssueModal = (index: number) => {
    const prescription = prescriptions?.[index]
    prescription && setSelectedPrescription(prescription)
    setReIssueModalIsOpen(true)
  }

  const handleRenewScript = useCallback(
    (prescription: Prescription) => {
      runWithValidSubscription(async () => {
        const renewalPatientId = prescription.issueSnapshot.patient.id
        if (!canPrescribeFor(renewalPatientId)) {
          toast({
            title: 'This prescription is for another EHR patient',
            description:
              'Select that patient in the EHR and begin a new launch before renewing the prescription.',
            status: 'warning',
            duration: 5000,
            isClosable: true,
            position: 'top',
          })
          return
        }
        setRenewingScid(prescription.scid)
        try {
          await prefillPrescriptionForRenew({
            prescription,
            dispatch,
            defaultPrescriptionDeliveryMethod,
            preserveSelectedPatient: authenticatedProvider === 'ehr',
          })
          const newScriptState = {
            renewSourceScid: prescription.scid,
          }
          navigate(routes.newScript.path, { state: newScriptState })
        } catch (error) {
          console.error('Error prefilling prescription for renew', error)
          toast({
            title: 'Unable to renew prescription',
            description: 'Some prescription details could not be loaded. Please try again.',
            status: 'error',
            duration: 5000,
            isClosable: true,
            position: 'top',
          })
        } finally {
          setRenewingScid(null)
        }
      })
    },
    [
      authenticatedProvider,
      canPrescribeFor,
      defaultPrescriptionDeliveryMethod,
      dispatch,
      navigate,
      runWithValidSubscription,
      toast,
    ]
  )

  const handleSearch = (value: string) => {
    const formattedValue = value.trim()
    setSearchTerm(formattedValue)
    setPage(1)

    if (formattedValue) {
      setSearchParams({ search: formattedValue })
    } else {
      setSearchParams({})
    }
  }

  const renewingPatientName = useMemo(() => {
    if (!renewingScid || !prescriptions) {
      return undefined
    }
    const prescription = prescriptions.find((item) => item.scid === renewingScid)
    if (!prescription) {
      return undefined
    }
    const { givenNames, familyName } = prescription.issueSnapshot.patient
    return `${givenNames ?? ''} ${familyName ?? ''}`.trim() || undefined
  }, [prescriptions, renewingScid])

  if (!isPrescriptionAdmin && !currentUserPrescriberProfile) {
    return <UnauthorizedPage />
  }

  const isReadonlyPermission = !currentUserPrescriberProfile?.active

  const selectorSelectedPrescriberId =
    adminScopePrescriber?.id ?? currentUserPrescriberId ?? legacyDefaultPrescriber?.id

  const isForbiddenError =
    error && typeof error === 'object' && 'status' in error && error.status === 403
  const errorMessage = isForbiddenError
    ? 'You do not have permission to view these prescriptions.'
    : 'Something went wrong!'

  return (
    <Box display="flex" flexDirection="column" overflow="hidden" p="12px">
      {isLoading && <Loading />}
      <RenewPrescriptionLoadingModal isOpen={!!renewingScid} patientName={renewingPatientName} />
      {!!error && !isLoading && (
        <Center bg="tomato" minH="100px" color="white" mb="12px" px="4" borderRadius="md">
          <Text>{errorMessage}</Text>
        </Center>
      )}
      <VStack spacing={4} align="stretch" height="100%" width="100%" overflow="hidden">
        <Flex gap={4} flexDirection={{ base: 'column', md: 'row' }} justifyContent="space-between">
          <Text as="h1" fontSize="3xl" fontWeight="bold">
            Prescriptions
          </Text>{' '}
        </Flex>
        <Flex gap={4} flexDirection={{ base: 'column', md: 'row' }} justifyContent="space-between">
          <Box flex={1}>
            <SearchInput
              w="100%"
              alignSelf="flex-start"
              onSearch={handleSearch}
              placeholder="Search by patient name, SCID or item..."
              initialValue={searchTerm}
            />
          </Box>

          <Flex gap={4} align="center" flexWrap="wrap">
            {isMultipleLocationsFeatureOn && (
              <LocationFilter
                selectedLocationId={selectedLocationId}
                onChange={(id) => setSelectedLocationId(id)}
              />
            )}

            {canSharePrescriptionsAcrossOrg && currentUserPrescriberProfile && (
              <Flex
                align="center"
                gap="2"
                bg="white"
                px="3"
                py="2.5"
                borderRadius="lg"
                border="1px"
                borderColor="gray.200"
                width={{ base: 'full', md: 'fit-content' }}
                flexShrink="0"
              >
                <Icon as={FiUsers} color="#2563eb" />
                <Text
                  fontSize="sm"
                  fontWeight="medium"
                  color="gray.700"
                  whiteSpace="nowrap"
                  flex={1}
                >
                  Show only my prescriptions
                </Text>
                <Switch
                  aria-label="Show only my prescriptions"
                  isChecked={showOnlyMyPrescriptions}
                  onChange={(e) => dispatch(setShowOnlyMyPrescriptions(e.target.checked))}
                  size="sm"
                  colorScheme="blue"
                />
              </Flex>
            )}

            {isPrescriptionAdmin && !prescriberId && !canSharePrescriptionsAcrossOrg && (
              <Flex align="center" gap={3} zIndex={2}>
                <Text fontSize="sm" fontWeight="medium" color="gray.700" whiteSpace="nowrap">
                  Selected prescriber:
                </Text>
                <PrescriberSelector
                  users={prescribers}
                  selectedPrescriberId={selectorSelectedPrescriberId}
                  onSelect={(id) =>
                    setAdminScopePrescriber(
                      prescribers.find((u) => u.prescriber?.id === id)?.prescriber ?? null
                    )
                  }
                />
              </Flex>
            )}
          </Flex>
        </Flex>

        {prescribers.length === 0 ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="200px"
            width="100%"
            borderWidth="1px"
            borderRadius="lg"
            p={4}
          >
            <Text fontSize="lg" color="gray.500">
              No prescribers available in this organisation.
            </Text>
          </Box>
        ) : !prescriptionScope ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="200px"
            width="100%"
            borderWidth="1px"
            borderRadius="lg"
            p={4}
          >
            <Text fontSize="lg" color="gray.500">
              Select a prescriber to view prescriptions.
            </Text>
          </Box>
        ) : prescriptions ? (
          <Fragment>
            <PrescriptionTable
              prescriptions={prescriptions}
              openCeaseModal={openCeaseModal}
              openReIssueModal={openReIssueModal}
              openCancelModal={openCancelModal}
              onRenewScript={handleRenewScript}
              renewingScid={renewingScid}
              isReadonlyPermission={isReadonlyPermission}
              isPrescriptionAdmin={isPrescriptionAdmin}
              viewerActive={currentUserPrescriberProfile?.active === true}
              prescriberActiveById={prescriberActiveById}
              shareRxAcrossOrg={canSharePrescriptionsAcrossOrg}
              isMultipleLocationsFeatureOn={isMultipleLocationsFeatureOn}
              currentUserPrescriberId={currentUserPrescriberId}
              canRenewScript={canRenewScript}
            />

            {prescriptions?.length > 0 && (
              <Box pt={2}>
                <RCPagination
                  showLessItems
                  current={page}
                  total={prescriptionsPaginationData?.totalItems}
                  pageSize={10}
                  onChange={(currentPage) => setPage(currentPage)}
                />
              </Box>
            )}
          </Fragment>
        ) : null}
      </VStack>

      <CeasePrescriptionModal isOpen={isCeaseOpen} onClose={onCeaseClose} scid={selectedScid} />

      {selectedPrescription && (
        <>
          <CancelPrescriptionModal
            isOpen={isCancelModalOpen}
            onClose={() => setIsCancelModalOpen(false)}
            prescription={selectedPrescription}
          />

          <ReIssuePrescriptionModal
            isOpen={reIssueModalIsOpen}
            onClose={() => {
              setReIssueModalIsOpen(false)
              setSelectedPrescription(undefined)
            }}
            prescriptions={[selectedPrescription]}
          />
        </>
      )}
    </Box>
  )
}

export default PrescriptionsPage
